import { useMemo } from "react";

import { isLegacySKU } from "../../../common/variables/skuOptions";
import { IDeviceComponent, ISKUGroup } from "../types/groupedDevices";
import { IDeviceTableRow, useDeviceTableRows } from "./useDeviceTableRows";

interface IUseGroupedDeviceTableRows {
  groups: ISKUGroup[];
  loading: boolean;
}

/**
 * Transforms flat device rows into grouped structure by serialNumber (SKU SN).
 * - Devices with the same serialNumber are grouped together
 * - Devices without serialNumber become individual "ungrouped" entries
 * - Legacy devices are marked as such
 * @return {IUseGroupedDeviceTableRows} Groups of devices organized by SKU Serial Number
 */
export const useGroupedDeviceTableRows = (): IUseGroupedDeviceTableRows => {
  const { rows, loading } = useDeviceTableRows();

  const groups = useMemo((): ISKUGroup[] => {
    if (loading || !rows.length) {
      return [];
    }

    // Map to group devices by serialNumber
    const groupMap = new Map<string, IDeviceTableRow[]>();

    rows.forEach(row => {
      // Use serialNumber as grouping key, or create unique key for ungrouped
      const groupKey = row.serialNumber || `ungrouped-${row.rowId}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }

      groupMap.get(groupKey)!.push(row);
    });

    // Transform map to ISKUGroup array
    const result: ISKUGroup[] = [];

    groupMap.forEach((deviceRows, groupKey) => {
      // Use first device for group-level data (all should have same SKU info)
      const firstDevice = deviceRows[0];
      const hasSerialNumber = Boolean(firstDevice.serialNumber);

      // Transform device rows to IDeviceComponent
      const devices: IDeviceComponent[] = deviceRows.map(row => ({
        rowId: row.rowId,
        deviceName: row.name,
        node: {
          id: row.node.id,
          name: row.node.name ?? row.node.id.split("#N#").at(-1) ?? "",
        },
        cameraIpAddress: row.cameraIpAddress,
        isOnline: row.isOnline,
        models: row.models,
        modelsToRun: row.modelsToRun,
        componentSerialNumber: row.componentSerialNumber,
        originalRow: row,
      }));

      // Aggregate status: all devices must be online for group to show "Online"
      const isAllOnline = devices.every(d => d.isOnline);

      const group: ISKUGroup = {
        groupId: groupKey,
        skuNumber: firstDevice.skuNumber ?? null,
        serialNumber: firstDevice.serialNumber ?? null,
        configurationName: firstDevice.configurationName ?? null,
        isAllOnline,
        devices,
        isSingleDevice: !hasSerialNumber,
        isLegacy: isLegacySKU(firstDevice.skuNumber),
      };

      result.push(group);
    });

    // Sort groups: SKU groups first (by skuNumber), then ungrouped/legacy last
    result.sort((a, b) => {
      // Legacy and ungrouped go to the end
      if (a.isSingleDevice && !b.isSingleDevice) return 1;

      if (!a.isSingleDevice && b.isSingleDevice) return -1;

      if (a.isLegacy && !b.isLegacy) return 1;

      if (!a.isLegacy && b.isLegacy) return -1;

      // Sort by SKU number for grouped items
      if (a.skuNumber && b.skuNumber) {
        return a.skuNumber.localeCompare(b.skuNumber);
      }

      if (a.skuNumber && !b.skuNumber) return -1;

      if (!a.skuNumber && b.skuNumber) return 1;

      // Sort by serial number if SKU is same
      if (a.serialNumber && b.serialNumber) {
        return a.serialNumber.localeCompare(b.serialNumber);
      }

      return 0;
    });

    return result;
  }, [rows, loading]);

  return {
    groups,
    loading,
  };
};
